/**
 * Clears the KiTbetter session before handing a logout click over to Shopify.
 *
 * Port of shopify-en-store-front app/hooks/useLogout.ts, which wiped the cookies itself and
 * only then navigated to /account/logout — the route can end the Shopify session
 * (secure_customer_sig) but knows nothing about the cookies KiTbetter keeps on
 * .kitbetter.com. Without this, "sign out" left accessToken / refreshToken / userInfo behind,
 * so script-gnb-user-type.js kept resolving a live user_type and the nav gates stayed open for
 * someone who had just logged out.
 *
 * Markup contract: any element carrying [data-logout]. The handler is delegated from the
 * document in the capture phase, so it runs before the link navigates no matter which
 * dropdown or drawer the link happens to sit in, and it does not preventDefault — the href
 * (routes.account_logout_url) stays the thing that performs the logout.
 *
 * Two things this deliberately does NOT do:
 *   - No multipass_token. Multipass is deprecated and being removed team-wide, so the
 *     reference's legacy line for it is not carried over.
 *   - No `cart`. The reference dropped the cart token on logout; here the cart follows
 *     Shopify's own behaviour and survives, so a shopper who signs out keeps their basket.
 *
 * Dropping accessToken really does end the KiTbetter side of the login, rather than only
 * hiding it from this theme (2026-08-20, measured against fanz-api.kitbetter.com/oidc/authorize
 * with a client_id of shopify_kitbetter_us):
 *   no cookies                  → 200, empty body   (the anonymous dead end)
 *   refreshToken only           → 200, empty body   (the endpoint does not read it)
 *   accessToken, malformed      → 400 "Unable to read JSON value"
 * So the IdP authenticates a top-level navigation from that one cookie and keeps no session of
 * its own — the anonymous response sets no cookie either. Once it is gone, a login click lands
 * on the empty page instead of being answered with a code, which is exactly the state the
 * logged-out flow already assumes (login buttons go to KiTbetter, never to /account/login).
 *
 * What this is still not: a server-side revoke. The refreshToken value stays valid for its
 * natural 7 days, so this ends the session in this browser, not everywhere.
 */

/**
 * Both the prod and the test name for every env-paired cookie, rather than picking one by
 * hostname the way script-gnb-user-type.js has to. That file *reads* a cookie, so it must know
 * which one is authoritative; here an expiring write for a cookie that was never set is a
 * no-op, and clearing both means a shopper carrying the other env's cookie (the studio and the
 * storefront share the domain) is not left half signed in.
 *
 * Names come from @kitbetter-web/variable and @kitbetter-web/packages-constants, via
 * clearUserSession() in @kitbetter-web/packages-core.
 */
const KITBETTER_COOKIE_NAMES = [
  'accessToken',
  'accessTokenTest',
  'refreshToken',
  'refreshTokenTest',
  'userInfo',
  'userInfoTest',
  'artist_id',
  'artist_id_test',
  'showWelcomeModal',
  'shopify_access_token',
  // Written by script-gnb-user-type.js, not by KiTbetter — the cached nav gate and its user id.
  'user_type',
  'user_id',
  // Pre-OIDC leftovers the reference still swept up.
  'customer_id',
];

/** sessionStorage key from packages-constants (SWITCHED_TO_ARTIST_FROM_INVITE_FLAG). */
const ARTIST_INVITE_FLAG_KEY = 'switchedToArtistFromInvite';

/**
 * A cookie is identified by name + domain + path, so an expiring write has to match the
 * domain the cookie was set with. We do not know it: KiTbetter pins its own cookies to
 * .kitbetter.com, but user_type / user_id are written by script-gnb-user-type.js, which only
 * pins the domain on that host and leaves them host-only everywhere else. So every domain this
 * document is allowed to write is tried. Walking up stops two labels short of the end because
 * the browser rejects a Domain of a public suffix such as `.com`.
 *
 * null means "no Domain attribute", which is how host-only cookies have to be cleared; a
 * Domain=<current host> write creates a *different*, domain-scoped cookie instead of removing
 * the host-only one, so both forms are needed.
 */
const cookieDomains = () => {
  const { hostname } = window.location;
  // An IP (theme dev serves 127.0.0.1) or a single-label host takes no Domain at all.
  if (!hostname.includes('.') || /^[\d.]+$/.test(hostname)) return [null];

  const labels = hostname.split('.');
  const domains = [null];
  for (let i = 0; i <= labels.length - 2; i += 1) {
    domains.push(`.${labels.slice(i).join('.')}`);
  }
  return domains;
};

const expireCookie = (name, domain) => {
  const parts = [
    `${encodeURIComponent(name)}=`,
    'Path=/',
    'Expires=Thu, 01 Jan 1970 00:00:00 GMT',
    'Max-Age=0',
  ];
  if (domain) parts.push(`Domain=${domain}`);
  if (window.location.protocol === 'https:') parts.push('Secure');
  document.cookie = parts.join('; ');
};

/**
 * Synchronous on purpose: the click that calls this is about to navigate, and document.cookie
 * writes land before it does. Anything awaited here would race the unload.
 */
const clearSession = () => {
  const domains = cookieDomains();

  KITBETTER_COOKIE_NAMES.forEach((name) => {
    domains.forEach((domain) => expireCookie(name, domain));
  });

  try {
    sessionStorage.removeItem(ARTIST_INVITE_FLAG_KEY);
  } catch (error) {
    // Safari private browsing throws on sessionStorage; the cookies are the part that matters.
  }
};

document.addEventListener(
  'click',
  (event) => {
    const trigger = event.target instanceof Element ? event.target.closest('[data-logout]') : null;
    if (!trigger) return;

    // Reference: GTMlogoutEvent({method: 'self'}) in app/hooks/useLogout.ts. Pushed before
    // the cookies go, so user_type is still on the dataLayer when the event is read.
    window.GTM?.logout({ method: 'self' });

    clearSession();
  },
  true
);
